/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import './fonts';

import { clamp } from '../../utils/src/clamp-text';
import { uuidv4 } from '../../utils/src/uuid-util';

export const FontWeight = {
  FiraExtraLight: '400',
  FiraExtraBold: '700',
  ExtraLight: '200',
  ExtraLightItalic: '200i',
  Light: '300',
  LightItalic: '300i',
  Regular: '400',
  Italic: '400i',
  Medium: '500',
  SemiBold: '600',
  SemiBoldItalic: '600i',
  Bold: '700',
  BoldItalic: '700i',
  ExtraBold: '800',
  ExtraBoldItalic: '800i',
  Black: '900',
  BlackItalic: '900i',
  None: 'none'
};

const propTypes = {
  secondary: PropTypes.bool,
  weight: PropTypes.string,
  spaceLeft: PropTypes.bool,
};

const defaultProps = {
  secondary: false,
  weight: FontWeight.Regular,
  spaceLeft: false,
};

// Mixins

export const FiraSansExtraLight = css`
  font-family: 'FiraSans', sans-serif;
  font-weight: 400;
  font-style: normal;
`;

export const FiraSansBold = css`
  font-family: 'FiraSans', sans-serif;
  font-weight: bold;
  font-style: normal;
`;

export const QuicksandRegular = css`
  font-family: 'Quicksand';
  font-weight: normal;
  font-style: normal;
`;

export const QuicksandBold = css`
  font-family: 'Quicksand';
  font-weight: bold;
  font-style: normal;
`;

export const QuicksandMedium = css`
  font-family: 'Quicksand';
  font-weight: 500;
  font-style: normal;
`;

export const QuicksandSemiBold = css`
  font-family: 'Quicksand';
  font-weight: 600;
  font-style: normal;
`;

export const QuicksandLight = css`
  font-family: 'Quicksand';
  font-weight: 300;
  font-style: normal;
`;



const Quicksand = css`
  ${QuicksandRegular}

  ${({ weight }) => weight === FontWeight.Light && QuicksandLight}
  ${({ weight }) => weight === FontWeight.Medium && QuicksandMedium}
  ${({ weight }) => weight === FontWeight.SemiBold && QuicksandSemiBold}
  ${({ weight }) => weight === FontWeight.Bold && QuicksandBold}
`;

const FiraSans = css`
  ${FiraSansExtraLight}

  ${({ weight }) => weight === FontWeight.Bold && FiraSansBold}
  
`;

// Components

const StyledText = styled.span`
  letter-spacing: -0.25px;
  ${FiraSans}
  ${({ numberOfLines }) =>
    numberOfLines &&
    css`
      display: block;
    `}

  & strong {
    font-weight: bold;
  }

  ${({ spaceLeft }) =>
    spaceLeft
      ? css`
      margin: 0px 0px 0px 5px;
    `
      : ``
  }
`;

const Text = ({ numberOfLines, children, ...props }) => {
  const id = useRef(numberOfLines ? uuidv4() : null);

  useEffect(() => {
    if (numberOfLines && id && id.current) {
      clamp(document.getElementById(id.current), { clamp: numberOfLines });
    }
  }, [numberOfLines, children, id]);

  return (
    <StyledText
      {...(id.current ? { id: id.current } : {})}
      numberOfLines={numberOfLines}
      {...props}
    >
      {children}
    </StyledText>
  );
};

Text.propTypes = propTypes;
Text.defaultProps = defaultProps;

export const RegularText = styled(Text).attrs({
  weight: FontWeight.Regular
})``;

export const SemiBoldText = styled(Text).attrs({
  weight: FontWeight.SemiBold
})``;

export const BoldText = styled(Text).attrs({
  weight: FontWeight.Bold
})``;

export const BoldItalicText = styled(Text).attrs({
  weight: FontWeight.BoldItalic
})``;

export const ExtraBoldText = styled(Text).attrs({
  weight: FontWeight.ExtraBold
})``;

export default styled(Text)``;
